import { OrbitControls } from "@react-three/drei";
import { useFrame, useThree } from "@react-three/fiber";
import { useMediaQuery } from "@tosize/ui";
import gsap from "gsap";
import { Fragment, useCallback, useEffect, useRef } from "react";
import { Box3, PerspectiveCamera, Vector3 } from "three";
import type { OrbitControls as OrbitControlsImpl } from "three-stdlib";
import { useConfig } from "../../../../context/ConfigContext";
import { useCabinetStore } from "../../../../context/StoreContext";
import { useCanvasStore } from "../../../../hooks/useCanvasStore";
import { useDragContext } from "../../context/DragContext";
import { useDebug } from "../../debug";
import { calculcateCameraZPosition } from "../../utils";
import { SecretControls } from "./SecretControls";

export default function Controls() {
	const dragContext = useDragContext();
	const controlsRef = useRef<OrbitControlsImpl>(null);
	const { debugEnabled } = useDebug();
	const { mode } = useConfig();

	const three = useThree();
	const isTouchDevice = useMediaQuery({ pointer: "touch" });
	const { fullScreen, altControls } = useCanvasStore();
	const { state } = useCabinetStore();
	const [width, height, depth] = [state.width / 1000, state.height / 1000, state.depth / 1000];

	const figureDimensions = { width: 668 / 1000, height: 1805 / 1000 };
	const target = {
		x: 0,
		y: figureDimensions.height > height ? (figureDimensions.height - height) / 2 : 0,
		z: 0,
	};

	const fitCameraToCenteredObject = useCallback(
		async (camera: PerspectiveCamera, orbitControls?: OrbitControlsImpl) => {
			const duration = 0.75;
			const boundingBox = new Box3();
			const center = new Vector3(
				width / 2,
				Math.max(height, figureDimensions.height) / 2,
				depth / 2,
			);
			const size = new Vector3(width, Math.max(height, figureDimensions.height), depth);
			boundingBox.setFromCenterAndSize(center, size);
			boundingBox.getSize(size);

			const cameraZ = calculcateCameraZPosition(camera.fov, camera.aspect, size);

			gsap.to(camera.position, { x: 1, y: 1, z: cameraZ, duration });

			// set the far plane of the camera so that it easily encompasses the whole object
			const minZ = boundingBox.min.z;
			const cameraToFarEdge = minZ < 0 ? -minZ + cameraZ : cameraZ - minZ;

			camera.far = cameraToFarEdge * 3;
			camera.updateProjectionMatrix();

			if (orbitControls !== undefined) {
				// set camera to rotate around the center
				gsap.to(orbitControls.target, { x: target.x, y: target.y, z: target.z, duration });

				// prevent camera from zooming out far enough to create far plane cutoff
				orbitControls.maxDistance = cameraToFarEdge * 2;

				orbitControls.update();
			}
		},
		[width, height, depth, target.x, target.y, target.z],
	);

	// refit camera to object on width/height/depth change
	useEffect(() => {
		if (
			controlsRef.current &&
			!fullScreen &&
			!altControls &&
			three.camera instanceof PerspectiveCamera
		) {
			fitCameraToCenteredObject(three.camera, controlsRef.current);
		}
	}, [altControls, fullScreen, three.camera, fitCameraToCenteredObject]);

	useFrame((frame) => {
		if (controlsRef.current && altControls && !fullScreen) {
			const minPan = new Vector3(width / -2, height / -2, depth / -2);
			const maxPan = new Vector3(width / 2, height / 2, depth / 2);
			controlsRef.current.target.clamp(minPan, maxPan);

			frame.camera.updateProjectionMatrix();
			controlsRef.current.update();
		}
	});

	return (
		<Fragment>
			<OrbitControls
				makeDefault
				ref={controlsRef}
				target={[target.x, target.y, target.z]}
				enabled={!dragContext?.isDragging}
				minAzimuthAngle={debugEnabled ? Number.POSITIVE_INFINITY : -Math.PI / 2 + 0.25}
				maxAzimuthAngle={debugEnabled ? Number.POSITIVE_INFINITY : Math.PI / 2 - 0.25}
				minPolarAngle={debugEnabled ? 0 : 0 + 0.25}
				maxPolarAngle={debugEnabled ? Math.PI : Math.PI / 2}
				minDistance={1.5}
				enableDamping
				enablePan={isTouchDevice || altControls}
				enableZoom={isTouchDevice || altControls}
				enableRotate={!isTouchDevice || altControls}
			/>

			{mode !== "view" && <SecretControls controls={controlsRef.current} />}
		</Fragment>
	);
}
